import { Component, OnInit, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { LoggingService } from '../../core/services/logging.service';
import { AuthService } from '../../core/services/auth.service';

/**
 * Auth callback component - handles OAuth redirect from BFF.
 *
 * Success flow:
 * - BFF has already created session and redirected back
 * - This component redirects to requests - authGuard will handle session validation
 *
 * Error flow:
 * - BFF detected validation error (user not found, inactive, etc.)
 * - BFF redirects with error query parameter
 * - This component captures error and redirects to login page
 */
@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [],
  template: `
    <div class="auth-callback-container">
      <div class="loading-spinner">
        <h2>Completing authentication...</h2>
        <p>Redirecting...</p>
      </div>
    </div>
  `,
  styles: [
    `
      .auth-callback-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100vh;
        background-color: #f5f5f5;
      }
      .loading-spinner {
        text-align: center;
        padding: 2rem;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    `,
  ],
})
export class AuthCallbackComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly logger = inject(LoggingService);
  private readonly authService = inject(AuthService);

  ngOnInit(): void {
    // Check for error query parameter from BFF
    const error = this.route.snapshot.queryParamMap.get('error');

    if (error) {
      // BFF validation failed - redirect to login with error message
      this.logger.error('Auth callback received error from BFF: ' + error);
      this.authService.setAuthError(error);
      this.router.navigate(['/login']);
    } else {
      // BFF has already created session and redirected back
      // Let the authGuard handle session validation when navigating to home route
      this.logger.info(
        'Auth callback: BFF session created, redirecting to home route'
      );
      this.authService.validateSession().subscribe(() => {
        this.router.navigate([this.authService.getHomeRoute()]);
      });
    }
  }
}
